// page-backlog-review-aval.ts — Les trois sorties d'aval de la revue.
//
// Diffuser ce qui a été décidé, solliciter les décideurs, mesurer l'effet. Trois
// CTA de l'écran de revue, sans nouvel écran de navigation — parce que les trois
// prolongent la revue au lieu de constituer un travail à part.
//
// Ce que chacune garde de non négociable :
//
// 1. **Le journal** est actif dès la PREMIÈRE application, pas à la clôture : le
//    PMO diffuse au fil de l'eau plutôt que de refaire le travail dans un mail.
// 2. **La sollicitation** annonce AVANT l'envoi ce qui va réellement partir —
//    combien de notifications, combien d'e-mails, et si l'e-mail est configuré
//    sur cet environnement. Un compte rendu supposé serait découvert au comité.
// 3. **Le bilan** affiche son ancienneté et sa mention non masquable. On
//    qualifie la portée d'une mesure ; on n'en interdit pas la consultation.

interface AvalReco {
  id: number;
  pile: string;
  to_arbitrate: boolean;
  // Renseignés par l'écran de la revue quand il ouvre la demande d'arbitrage,
  // pour rappeler ce sur quoi on demande de trancher. Optionnels : `hasApplied`
  // et `hasFlagged` n'ont besoin que de `pile` et `to_arbitrate`.
  family?: string;
  title?: string;
  targets?: Array<{ type: string; id: number }>;
}

interface Recipient {
  id: number;
  display_name: string;
  trigram: string | null;
  sees: number;
}

interface RecipientsBody {
  flagged: number;
  recipients: Recipient[];
  last_requested_at: string | null;
  email_configured: boolean;
  cooldown_minutes: number;
}

interface WindowStats {
  population: number;
  updated_pct: number;
  closed_pct: number;
  progressed_pct: number;
  idle_pct: number;
  median_days_to_first_update: number | null;
  open_actions: number;
}

interface OutcomeBody {
  weeks_since_close: number;
  significant_after_weeks: number;
  window_days: number;
  direct: { applied_total: number; by_family: Record<string, number> };
  induced: { population: number; before: WindowStats; after: WindowStats };
  caveat: string;
}

export function hasApplied(recos: AvalReco[]): boolean {
  return recos.some((r) => r.pile === 'applied');
}

export function hasFlagged(recos: AvalReco[]): boolean {
  return recos.some((r) => r.to_arbitrate);
}

// ── 14.1 — le journal des décisions ──────────────────────────────────────────

export async function downloadDecisions(reviewId: number, btn?: HTMLButtonElement | null) {
  await api.withButton(btn, async () => {
    try {
      const out = await api.downloadBacklogDecisions(reviewId, `optimisations-${reviewId}.xlsx`);
      toast(t('review.aval.decisionsDone', { name: out.filename }));
    } catch (e: unknown) {
      toast(e instanceof Error ? e.message : t('common.error'), 'error');
    }
  });
}

// ── 14.2 — demander l'arbitrage ──────────────────────────────────────────────

function _recipientRow(r: Recipient): string {
  // Combien il en voit est affiché : cocher quelqu'un qui n'en voit aucune est
  // une erreur qu'il vaut mieux voir avant d'envoyer.
  const none = r.sees === 0;
  return `<label style="display:block;margin:4px 0${none ? ';opacity:.55' : ''}">
      <input type="checkbox" data-recipient="${r.id}" ${none ? 'disabled' : 'checked'}>
      <strong>${esc(r.display_name)}</strong>
      <span class="text-muted text-xs"> — ${esc(
        none ? t('review.aval.seesNone') : t('review.aval.sees', { n: String(r.sees) }),
      )}</span>
    </label>`;
}

/** Ce sur quoi on demande de trancher, en toutes lettres.
 *
 * Un compteur seul (« 7 recommandations sont marquées ») oblige à fermer la
 * fenêtre pour vérifier ce qu'on est en train d'envoyer — donc à envoyer sans
 * vérifier. La liste est figée : elle ne se coche pas, la sélection des
 * recommandations est le drapeau ⚑, posé en amont.
 */
function _flaggedListHtml(flagged: AvalReco[], familyLabel: (f: string) => string): string {
  if (!flagged.length) return '';
  const rows = flagged
    .map((r) => {
      const codes = (r.targets || [])
        .filter((tg) => tg.type === 'action')
        .map((tg) => `#${tg.id}`)
        .join(' + ');
      return `<li style="margin:3px 0">
        <span class="badge">${esc(familyLabel(r.family || ''))}</span>
        <span class="text-mono text-xs">${esc(codes)}</span>
        ${esc(r.title || '')}
      </li>`;
    })
    .join('');
  return `<ul style="margin:6px 0 0 18px;max-height:28vh;overflow:auto">${rows}</ul>`;
}

/** Destinataires libres, en plus des approbateurs proposés.
 *
 * Chips ajoutables et retirables, comme la saisie des R d'une fiche action. Un
 * `<select>` plutôt que `showUserPicker` : le picker est lui-même une modale,
 * et `openModal` ferme la précédente — l'ouvrir d'ici ferait disparaître la
 * demande d'arbitrage en cours de saisie.
 */
function _extraPickerHtml(): string {
  return `<div style="margin-top:12px">
      <div class="card-title" style="font-size:var(--fs-sm)">${esc(t('review.aval.extraRecipients'))}</div>
      <div class="flex gap-2" style="margin-top:6px;flex-wrap:wrap">
        <select id="arbitration-extra-select" style="flex:1;min-width:180px"></select>
        <button type="button" class="btn btn-secondary btn-sm" id="arbitration-extra-add">
          ${esc(t('review.aval.extraAdd'))}
        </button>
      </div>
      <div id="arbitration-extra-chips" class="flex gap-2" style="margin-top:6px;flex-wrap:wrap"></div>
    </div>`;
}

function _arbitrationBodyHtml(body: RecipientsBody): string {
  const rows = body.recipients.map(_recipientRow).join('');
  const last = body.last_requested_at
    ? `<div class="text-muted text-xs" style="margin-top:10px">ⓘ ${esc(
        t('review.aval.lastRequest', { date: fmtDateTime(body.last_requested_at) }),
      )}</div>`
    : '';
  // Cas dégradé annoncé AVANT l'envoi, pas découvert après : la notification
  // part toujours, l'e-mail seulement si le relais est configuré.
  const degraded = body.email_configured
    ? ''
    : `<div class="text-xs" style="color:var(--warn);margin-top:8px">⚠ ${esc(
        t('review.aval.noEmail'),
      )}</div>`;
  return `<div>
      <p>${esc(t('review.aval.flaggedCount', { n: String(body.flagged) }))}</p>
      ${_flaggedListHtml(_flagged, _familyLabel)}
      <div class="card-title" style="font-size:var(--fs-sm);margin-top:12px">${esc(t('review.aval.recipients'))}</div>
      ${rows || `<div class="empty" style="padding:8px">${esc(t('review.aval.noRecipient'))}</div>`}
      ${_extraPickerHtml()}
      <label style="display:block;margin-top:12px">${esc(t('review.aval.message'))}
        <textarea id="arbitration-message" rows="3" style="width:100%"
          placeholder="${esc(t('review.aval.messagePlaceholder'))}"></textarea>
      </label>
      ${degraded}${last}
    </div>`;
}

// Contexte de la modale ouverte : les marquées à rappeler, leur libellé de
// famille, et les destinataires ajoutés à la main. Remis à zéro à chaque
// ouverture — une demande ne doit jamais hériter des destinataires de la
// précédente.
let _flagged: AvalReco[] = [];
let _familyLabel: (f: string) => string = (f) => f;
let _extra: Array<{ id: number; label: string }> = [];

export function openArbitrationModal(
  reviewId: number,
  onDone: () => void,
  context?: { flagged?: AvalReco[]; familyLabel?: (f: string) => string },
): void {
  _flagged = context?.flagged || [];
  _familyLabel = context?.familyLabel || ((f) => f);
  _extra = [];
  openWideModal(
    t('review.aval.arbitrationTitle'),
    `<div class="loading">${esc(t('common.loading'))}</div>`,
    () => _sendArbitration(reviewId, onDone),
    t('review.aval.send'),
  );
  void _loadRecipients(reviewId);
}

function _renderExtraChips(): void {
  const host = document.getElementById('arbitration-extra-chips');
  if (!host) return;
  host.innerHTML = _extra
    .map(
      (u) => `<span class="tag-chip">${esc(u.label)}
        <button type="button" class="tag-remove" data-extra-remove="${u.id}"
          aria-label="${esc(t('common.remove'))}" title="${esc(t('common.remove'))}">✕</button>
      </span>`,
    )
    .join(' ');
  host.querySelectorAll<HTMLElement>('[data-extra-remove]').forEach((btn) => {
    btn.addEventListener('click', () => {
      _extra = _extra.filter((u) => u.id !== Number(btn.dataset.extraRemove));
      _renderExtraChips();
    });
  });
}

/** Remplit le sélecteur avec les utilisateurs actifs qui ne sont PAS déjà
 *  proposés comme approbateurs : les reproposer ferait deux cases pour la même
 *  personne, et un doublon dans la demande. */
async function _bindExtraPicker(proposed: ReadonlyArray<number>): Promise<void> {
  const select = document.getElementById('arbitration-extra-select') as HTMLSelectElement | null;
  const add = document.getElementById('arbitration-extra-add');
  if (!select || !add) return;
  const users = (
    (await api.getUsers()) as Array<{
      id: number;
      display_name?: string | null;
      name?: string | null;
      is_active?: boolean;
    }>
  ).filter((u) => u.is_active !== false && !proposed.includes(u.id));
  select.innerHTML = users
    .map((u) => `<option value="${u.id}">${esc(u.display_name || u.name || '')}</option>`)
    .join('');
  add.addEventListener('click', () => {
    const id = Number(select.value || 0);
    if (!id || _extra.some((u) => u.id === id)) return;
    const label = select.options[select.selectedIndex]?.text || String(id);
    _extra.push({ id, label });
    _renderExtraChips();
  });
}

async function _loadRecipients(reviewId: number): Promise<void> {
  const host = document.getElementById('modal-body');
  if (!host) return;
  try {
    const body = (await api.getArbitrationRecipients(reviewId)) as RecipientsBody;
    host.innerHTML = _arbitrationBodyHtml(body);
    await _bindExtraPicker(body.recipients.map((r) => r.id));
  } catch (e: unknown) {
    host.replaceChildren();
    const err = document.createElement('div');
    err.className = 'error';
    err.textContent = e instanceof Error ? e.message : t('common.error');
    host.appendChild(err);
  }
}

async function _sendArbitration(reviewId: number, onDone: () => void): Promise<void> {
  const ids: number[] = [];
  document.querySelectorAll<HTMLInputElement>('[data-recipient]').forEach((box) => {
    if (box.checked) ids.push(Number(box.dataset.recipient));
  });
  // Les ajoutés à la main comptent autant que les proposés. `Set` : un même
  // destinataire ne doit pas être sollicité deux fois.
  for (const u of _extra) if (!ids.includes(u.id)) ids.push(u.id);
  if (!ids.length) {
    toast(t('review.aval.pickOne'), 'error');
    return;
  }
  const message =
    (document.getElementById('arbitration-message') as HTMLTextAreaElement | null)?.value || '';
  try {
    const out = (await api.requestArbitration(reviewId, {
      recipient_ids: ids,
      message,
      base_url: window.location.origin,
    })) as { notified: number; emailing: number; skipped: Array<{ display_name: string }> };
    closeModal();
    // Ce qui est RÉELLEMENT parti, jamais ce qu'on espérait envoyer.
    toast(
      t('review.aval.sent', { n: String(out.notified), mails: String(out.emailing) }) +
        (out.skipped.length
          ? ' ' + t('review.aval.skipped', { n: String(out.skipped.length) })
          : ''),
    );
    onDone();
  } catch (e: unknown) {
    toast(e instanceof Error ? e.message : t('common.error'), 'error');
  }
}

// ── 14.3 — le bilan d'optimisation ───────────────────────────────────────────

function _pct(value: number): string {
  return `${value} %`;
}

function _delay(value: number | null): string {
  return value === null ? '—' : t('review.aval.days', { n: String(value) });
}

function _comparison(body: OutcomeBody): string {
  const weeks = Math.max(1, Math.round(body.window_days / 7));
  const rows: Array<[string, string, string]> = [
    [
      t('review.aval.updated'),
      _pct(body.induced.before.updated_pct),
      _pct(body.induced.after.updated_pct),
    ],
    [
      t('review.aval.closed'),
      _pct(body.induced.before.closed_pct),
      _pct(body.induced.after.closed_pct),
    ],
    [
      t('review.aval.progressed'),
      _pct(body.induced.before.progressed_pct),
      _pct(body.induced.after.progressed_pct),
    ],
    [t('review.aval.idle'), _pct(body.induced.before.idle_pct), _pct(body.induced.after.idle_pct)],
    [
      t('review.aval.reactivity'),
      _delay(body.induced.before.median_days_to_first_update),
      _delay(body.induced.after.median_days_to_first_update),
    ],
    [
      t('review.aval.openActions'),
      String(body.induced.before.open_actions),
      String(body.induced.after.open_actions),
    ],
  ];
  const lines = rows
    .map(
      ([label, before, after]) =>
        `<tr><td>${esc(label)}</td><td style="text-align:right">${esc(before)}</td>
         <td style="text-align:right;font-weight:600">${esc(after)}</td></tr>`,
    )
    .join('');
  return `<div class="table-wrap"><table>
      <thead><tr>
        <th></th>
        <th style="text-align:right">${esc(t('review.aval.before', { n: String(weeks) }))}</th>
        <th style="text-align:right">${esc(t('review.aval.after', { n: String(weeks) }))}</th>
      </tr></thead>
      <tbody>${lines}</tbody>
    </table></div>`;
}

function _outcomeBodyHtml(body: OutcomeBody): string {
  const families = Object.entries(body.direct.by_family)
    .map(([family, n]) => `<li>${esc(t('review.family.' + family))} : ${n}</li>`)
    .join('');
  // Avertit sans interdire : le serveur rend l'ancienneté, l'écran la qualifie.
  const young =
    body.weeks_since_close < body.significant_after_weeks
      ? `<div class="text-xs" style="color:var(--warn);margin-bottom:10px">⚠ ${esc(
          t('review.aval.tooYoung', { weeks: String(body.weeks_since_close) }),
        )}</div>`
      : '';
  return `<div>
      ${young}
      <div class="card-title" style="font-size:var(--fs-sm)">${esc(t('review.aval.direct'))}</div>
      <p><strong>${body.direct.applied_total}</strong> ${esc(t('review.aval.appliedTotal'))}</p>
      <ul style="margin:6px 0 14px 18px">${families}</ul>
      <div class="card-title" style="font-size:var(--fs-sm)">${esc(
        t('review.aval.induced', { n: String(body.induced.population) }),
      )}</div>
      ${_comparison(body)}
      <div class="text-muted text-xs" style="margin-top:12px">${esc(body.caveat)}</div>
    </div>`;
}

export function openOutcomeModal(reviewId: number): void {
  // `hideCancel` : il n'y a rien à confirmer, on lit un bilan.
  openWideModal(
    t('review.aval.outcomeTitle'),
    `<div class="loading">${esc(t('common.loading'))}</div>`,
    () => closeModal(),
    t('common.close'),
    true,
  );
  void _loadOutcome(reviewId);
}

async function _loadOutcome(reviewId: number): Promise<void> {
  const host = document.getElementById('modal-body');
  if (!host) return;
  try {
    const body = (await api.getBacklogOutcome(reviewId)) as OutcomeBody;
    host.innerHTML = _outcomeBodyHtml(body);
  } catch (e: unknown) {
    host.replaceChildren();
    const err = document.createElement('div');
    err.className = 'error';
    err.textContent = e instanceof Error ? e.message : t('common.error');
    host.appendChild(err);
  }
}

function fmtDateTime(iso: string): string {
  // Le serveur horodate en UTC naïf (pas de suffixe). Sans le `Z`, le
  // navigateur lit la chaîne comme une heure LOCALE : « dernière sollicitation »
  // s'affiche deux heures en arrière et contredit le message de cadence.
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
}
